/**
 * New project (docs/ux/workflow-simplification.md §E4): name + goal → repository (a new private
 * GitHub repo by default, or an existing remote) → machines (all online preselected) → Create →
 * ONE passkey approves the checkout on every chosen machine → per-machine progress → the project
 * page opens with its composer. Centered dialog (never bottom-opening). Everything the user types
 * stays end-to-end encrypted; the hub sees ids and digests only.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { CircleCheck, CircleDashed, CircleX, Laptop, Loader2, MessagesSquare } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import {
  type CheckoutDeps,
  type CheckoutProgress,
  checkoutOk,
  draftProblem,
  knownRemotes,
  type NewProjectDraft,
  newProjectDefaults,
  progressLabel,
  reportedRemote,
  requestCheckouts,
  slugify,
} from '../lib/account-projects.js'
import { useBackend } from '../lib/backend.js'
import { clock } from '../lib/clock.js'
import { loadDevice } from '../lib/device-key.js'
import { projectHref } from '../lib/ia.js' // ia-shell
import { passkeys } from '../lib/passkeys.js'
import { startScoping } from '../lib/project-setup.js' // g5-setup
import { qk, useMachines } from '../lib/queries.js'
import type { Machine } from '../lib/types.js'
import { useAppUi } from './app-ui.js'
import { useBarkStore } from './bark/useBarks.js'
import { DialogFooter } from './DialogFooter.js'
import { ScopeProjectDialog } from './setup/ScopeProjectDialog.js' // g5-setup
import { TendencyList } from './setup/TendencyList.js'
import { scopingDeps, useTendencies } from './setup/useProjectSetup.js'
import { Button, errorText, Sheet } from './ui.js'

/** The browser side of a checkout: account, device key, one passkey tap, signed dispatch. */
export function useCheckoutDeps(): CheckoutDeps {
  const b = useBackend()
  return useMemo(
    () => ({
      accountId: async () => {
        const id = await b.content?.accountId()
        if (!id) throw new Error('Account projects need an unlocked encrypted account.')
        return id
      },
      deviceKeyId: async () => {
        const dev = await loadDevice()
        if (!dev?.endorsement) throw new Error('Trust this device with a passkey first.')
        return dev.deviceKeyId
      },
      assert: (challenge) => passkeys.assert(challenge),
      dispatch: (kind, machineId, payload) => b.dispatch(kind, machineId, payload),
      now: () => clock.now(),
    }),
    [b],
  )
}

export function CheckoutRows({
  rows,
  machines,
  creating,
}: {
  rows: CheckoutProgress[]
  machines: readonly Machine[]
  creating: boolean
}) {
  if (!rows.length) return null
  return (
    <ul
      className="m-0 grid list-none gap-1.5 p-0"
      aria-label="Checkout progress"
      aria-live="polite"
    >
      {rows.map((r) => {
        const name = machines.find((m) => m.id === r.machineId)?.name ?? r.machineId
        const ok = checkoutOk(r)
        const bad = r.state === 'error' || (r.state === 'done' && !ok)
        const Icon =
          r.state === 'working' ? Loader2 : ok ? CircleCheck : bad ? CircleX : CircleDashed
        return (
          <li key={r.machineId} className="flex items-start gap-2 text-[14px]">
            <Icon
              size={16}
              aria-hidden="true"
              className={`mt-0.5 flex-none ${ok ? 'text-status-ok' : bad ? 'text-status-fail' : 'text-text-3'} ${r.state === 'working' ? 'animate-spin' : ''}`}
            />
            <span className="font-medium">{name}</span>
            <span className="min-w-0 break-words text-text-2">{progressLabel(r, creating)}</span>
          </li>
        )
      })}
    </ul>
  )
}

export function NewProjectDialog({
  onClose,
  onCreated,
}: {
  onClose(): void
  /** Lanes tab: pin and open the new project's tab instead of the projects page. */
  onCreated?: (projectId: string) => void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const ui = useAppUi()
  const deps = useCheckoutDeps()
  const machines = useMachines()
  const list = machines.data ?? []
  const [draft, setDraft] = useState<NewProjectDraft>(() => newProjectDefaults(list))
  const [touchedRepoName, setTouchedRepoName] = useState(false)
  const [rows, setRows] = useState<CheckoutProgress[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [createdId, setCreatedId] = useState<string>()
  const ids = { name: useId(), goal: useId(), owner: useId(), repo: useId(), remote: useId() }
  // Machines load after the dialog opens: preselect the online ones and the gh owner once.
  const [seeded, setSeeded] = useState(list.length > 0)
  if (!seeded && list.length) {
    setSeeded(true)
    setDraft((d) => ({ ...newProjectDefaults(list, d.name), goal: d.goal }))
  }
  // ---- g5-setup: learned tendencies + "Scope with an agent" ----
  const tendencies = useTendencies().data
  const barks = useBarkStore()
  const [scopingId, setScopingId] = useState<string>()
  // ---- end g5-setup ----
  const problem = draftProblem(draft, list)
  const remotes = knownRemotes(list)
  const defaultOwner = list.map((m) => m.projectDefaults?.ghUser).find(Boolean) ?? ''
  const creating = draft.repo.kind === 'create'

  const setName = (name: string) =>
    setDraft((d) => ({
      ...d,
      name,
      repo:
        d.repo.kind === 'create' && !touchedRepoName ? { ...d.repo, name: slugify(name) } : d.repo,
    }))

  const create = async () => {
    if (problem || busy) return
    setBusy(true)
    setError('')
    try {
      // 1. The encrypted account project (name, goal, remote) exists before any machine acts.
      const remote = draft.repo.kind === 'existing' ? draft.repo.remote.trim() : null
      const project =
        createdId !== undefined
          ? { id: createdId }
          : await b.api.createAccountProject({ name: draft.name.trim(), goal: draft.goal, remote })
      setCreatedId(project.id)
      // 2. One passkey → one project.checkout per machine.
      const online = draft.machineIds.filter((id) => list.find((m) => m.id === id)?.online)
      const { statement, results } = await requestCheckouts(
        deps,
        {
          projectId: project.id,
          machineIds: online,
          repo: draft.repo,
          dirName: slugify(draft.repo.kind === 'create' ? draft.repo.name : draft.name),
          name: draft.name.trim(),
          ...(draft.goal.trim() ? { goal: draft.goal.trim() } : {}),
          author: true,
        },
        (p) => setRows((cur) => [...cur.filter((r) => r.machineId !== p.machineId), p]),
      )
      const done = results.filter(checkoutOk).map((r) => r.machineId)
      const newRemote = reportedRemote(results)
      await b.api.recordCheckouts(project.id, {
        machineIds: online,
        statementId: statement.statementId,
        via: 'checkout',
        ...(newRemote ? { remote: `https://${newRemote}` } : {}),
      })
      await qc.invalidateQueries({ queryKey: qk.projects })
      await qc.invalidateQueries({ queryKey: qk.machines })
      if (done.length) {
        onClose()
        if (onCreated) onCreated(project.id)
        else void nav({ href: projectHref(project.id) })
        ui.openStart({ projectId: project.id })
      } else setError('No machine finished the checkout. Fix the issue above and try again.')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  // g5-setup: a discuss-first scoping conversation on the first chosen machine; the project
  // entity exists at once (so the conversation and research are saved with it), nothing else.
  const scope = async () => {
    if (problem || busy) return
    const machineId = draft.machineIds.find((id) => list.find((m) => m.id === id)?.online)
    if (!machineId) return
    setBusy(true)
    setError('')
    try {
      const r = await startScoping(scopingDeps(b, barks), {
        name: draft.name,
        goal: draft.goal,
        repo: draft.repo,
        machineId,
        ...(tendencies ? { tendencies } : {}),
        ...(createdId ? { projectId: createdId } : {}),
      })
      setCreatedId(r.projectId)
      await qc.invalidateQueries({ queryKey: qk.projects })
      setScopingId(r.projectId)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  if (scopingId)
    return (
      <ScopeProjectDialog
        projectId={scopingId}
        onClose={onClose}
        onCreated={(id) => {
          if (onCreated) onCreated(id)
          else void nav({ href: projectHref(id) })
        }}
      />
    )

  return (
    <Sheet
      open
      onClose={onClose}
      title="New project"
      subtitle="Name, goal and repository stay end-to-end encrypted."
      placement="center"
      size="md"
    >
      <div className="dlg-body gap-5">
        <div className="grid gap-3">
          <label className="field" htmlFor={ids.name}>
            <span>Name</span>
            <input
              id={ids.name}
              className="input"
              value={draft.name}
              disabled={busy || !!createdId}
              // biome-ignore lint/a11y/noAutofocus: first field of the dialog
              autoFocus
              placeholder="Tide Clock"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="field" htmlFor={ids.goal}>
            <span>Goal</span>
            <textarea
              id={ids.goal}
              className="input min-h-[72px] resize-y"
              rows={2}
              value={draft.goal}
              disabled={busy || !!createdId}
              placeholder="iOS widget showing local tides"
              onChange={(e) => setDraft((d) => ({ ...d, goal: e.target.value }))}
            />
          </label>
        </div>

        <fieldset className="grid gap-2 border-0 p-0" disabled={busy || !!createdId}>
          <legend className="mb-1 text-[14px] font-semibold">Repository</legend>
          <div className="choice" role="radiogroup" aria-label="Repository">
            <button
              type="button"
              role="radio"
              aria-checked={creating}
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  repo: { kind: 'create', owner: defaultOwner, name: slugify(d.name) },
                }))
              }
            >
              Create new private GitHub repo
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!creating}
              onClick={() => setDraft((d) => ({ ...d, repo: { kind: 'existing', remote: '' } }))}
            >
              Use existing
            </button>
          </div>
          {draft.repo.kind === 'create' ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="field min-w-[8rem] flex-1" htmlFor={ids.owner}>
                <span>Owner</span>
                <input
                  id={ids.owner}
                  className="input"
                  value={draft.repo.owner}
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="GitHub user or org"
                  onChange={(e) =>
                    setDraft((d) =>
                      d.repo.kind === 'create'
                        ? { ...d, repo: { ...d.repo, owner: e.target.value.trim() } }
                        : d,
                    )
                  }
                />
              </label>
              <span className="pb-2 text-text-3" aria-hidden="true">
                /
              </span>
              <label className="field min-w-[8rem] flex-[2]" htmlFor={ids.repo}>
                <span>Repository name</span>
                <input
                  id={ids.repo}
                  className="input"
                  value={draft.repo.name}
                  autoCapitalize="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setTouchedRepoName(true)
                    setDraft((d) =>
                      d.repo.kind === 'create'
                        ? { ...d, repo: { ...d.repo, name: e.target.value.trim() } }
                        : d,
                    )
                  }}
                />
              </label>
            </div>
          ) : (
            <label className="field" htmlFor={ids.remote}>
              <span>Repository URL</span>
              <input
                id={ids.remote}
                className="input"
                list={`${ids.remote}-seen`}
                value={draft.repo.remote}
                autoCapitalize="off"
                spellCheck={false}
                placeholder="git@github.com:you/app.git"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, repo: { kind: 'existing', remote: e.target.value } }))
                }
              />
              <datalist id={`${ids.remote}-seen`}>
                {remotes.map((r) => (
                  <option key={r} value={`https://${r}`} />
                ))}
              </datalist>
              <span className="field__hint">
                A machine that already has a clone of it adopts that clone instead of cloning again.
              </span>
            </label>
          )}
        </fieldset>

        <fieldset className="grid gap-2 border-0 p-0" disabled={busy || !!createdId}>
          <legend className="mb-1 text-[14px] font-semibold">Machines</legend>
          {list.length === 0 ? (
            <p className="m-0 text-[14px] text-text-3">
              No machines connected yet. Set up viko on your Mac or PC first.
            </p>
          ) : (
            list.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  disabled={!m.online}
                  checked={m.online && draft.machineIds.includes(m.id)}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      machineIds: e.target.checked
                        ? [...d.machineIds, m.id]
                        : d.machineIds.filter((x) => x !== m.id),
                    }))
                  }
                />
                <Laptop size={14} aria-hidden="true" className="text-text-3" />
                <span className="font-medium">{m.name}</span>
                {m.online ? null : (
                  <span className="text-text-3">
                    Offline · check out later from the project page
                  </span>
                )}
              </label>
            ))
          )}
          <p className="m-0 text-[14px] text-text-3">
            One passkey approves the checkout on every machine you pick. Each clones into{' '}
            {list.find((m) => m.projectDefaults)?.projectDefaults?.devRoot ?? '~/Documents/dev'}.
          </p>
        </fieldset>

        {/* ---- g5-setup: what the defaults are learned from ---- */}
        {tendencies?.items.length ? <TendencyList tendencies={tendencies} /> : null}
        {/* ---- end g5-setup ---- */}
        <CheckoutRows rows={rows} machines={list} creating={creating} />
        {error ? (
          <p role="alert" className="m-0 rounded-lg bg-status-fail-soft px-3 py-2 text-[14px]">
            {error}
          </p>
        ) : problem && draft.name ? (
          <p className="m-0 text-[14px] text-text-3">{problem}</p>
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          disabled={!!problem || busy || !barks}
          title={problem || (!barks ? 'Unlock this browser to talk with the agent.' : undefined)}
          icon={<MessagesSquare size={14} />}
          onClick={() => void scope()}
        >
          Scope with an agent
        </Button>
        <Button
          variant="primary"
          disabled={!!problem || busy}
          title={problem || undefined}
          onClick={() => void create()}
        >
          {busy ? 'Creating…' : createdId ? 'Try again' : 'Create'}
        </Button>
      </DialogFooter>
    </Sheet>
  )
}
